import type { ConfigurationParameters, TokenResponse } from '@app/types/sdk';

type AccessToken = NonNullable<ConfigurationParameters['accessToken']>;

export type AccessTokenResolver = Extract<AccessToken, (...args: any[]) => any>;

const ACCESS_TOKEN_STORAGE_KEY = 'reap.accessToken';
const REFRESH_TOKEN_STORAGE_KEY = 'reap.refreshToken';
const ACCESS_TOKEN_EXPIRY_KEY = 'reap.accessToken.expiresAt';
const AUTH_STORAGE_KEYS = [ACCESS_TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY, ACCESS_TOKEN_EXPIRY_KEY] as const;

const getTokenStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getLegacyTokenStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const clearAuthKeys = (storage: Storage | null): void => {
  if (!storage) {
    return;
  }

  for (const key of AUTH_STORAGE_KEYS) {
    storage.removeItem(key);
  }
};

const migrateLegacyTokens = (storage: Storage): void => {
  const legacyStorage = getLegacyTokenStorage();
  if (!legacyStorage) {
    return;
  }

  const accessToken = legacyStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (!accessToken || storage.getItem(ACCESS_TOKEN_STORAGE_KEY)) {
    clearAuthKeys(legacyStorage);
    return;
  }

  storage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);

  const refreshToken = legacyStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  if (refreshToken) {
    storage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  }

  const expiresAt = legacyStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
  if (expiresAt) {
    storage.setItem(ACCESS_TOKEN_EXPIRY_KEY, expiresAt);
  }

  clearAuthKeys(legacyStorage);
};

export const buildAccessTokenProvider = (): AccessTokenResolver => {
  return async () => {
    const storage = getTokenStorage();
    if (!storage) {
      return '';
    }
    migrateLegacyTokens(storage);

    const token = storage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (token) {
      window.__REAP_ACCESS_TOKEN__ = token;
      return token;
    }

    delete window.__REAP_ACCESS_TOKEN__;
    return '';
  };
};

export interface PersistedToken {
  accessToken: string;
  refreshToken?: string | null;
  expiresAt?: number;
  tokenType?: string;
}

export function persistTokenResponse(payload: TokenResponse): PersistedToken {
  const expiresAt = typeof payload.expiresIn === 'number' ? Date.now() + payload.expiresIn * 1000 : undefined;
  const bundle: PersistedToken = {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    expiresAt,
    tokenType: payload.tokenType
  };

  if (typeof window !== 'undefined') {
    const storage = getTokenStorage();
    window.__REAP_ACCESS_TOKEN__ = payload.accessToken;
    if (!storage) {
      return bundle;
    }
    storage.setItem(ACCESS_TOKEN_STORAGE_KEY, payload.accessToken);

    if (payload.refreshToken) {
      storage.setItem(REFRESH_TOKEN_STORAGE_KEY, payload.refreshToken);
    } else {
      storage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }

    if (expiresAt) {
      storage.setItem(ACCESS_TOKEN_EXPIRY_KEY, String(expiresAt));
    } else {
      storage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
    }
  }

  return bundle;
}

export function readStoredTokens(): PersistedToken | null {
  const storage = getTokenStorage();
  if (!storage) {
    return null;
  }
  migrateLegacyTokens(storage);

  const accessToken = storage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (!accessToken) {
    delete window.__REAP_ACCESS_TOKEN__;
    return null;
  }
  window.__REAP_ACCESS_TOKEN__ = accessToken;

  const refreshToken = storage.getItem(REFRESH_TOKEN_STORAGE_KEY) ?? undefined;
  const expiresRaw = storage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
  const expiresAt = expiresRaw ? Number.parseInt(expiresRaw, 10) : undefined;

  return {
    accessToken,
    refreshToken,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : undefined
  };
}

export function clearStoredTokens(): void {
  delete window.__REAP_ACCESS_TOKEN__;
  clearAuthKeys(getTokenStorage());
  clearAuthKeys(getLegacyTokenStorage());
}

declare global {
  interface Window {
    __REAP_ACCESS_TOKEN__?: string;
  }
}
